import { useEffect, useRef } from "react";
import { Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";

/**
 * Gates venue-scoped routes that should only be accessible from a kiosk that
 * the Venue Admin has unlocked via PIN. Without this gate, anyone with the
 * venue URL could remotely sign visitors / staff in or out.
 *
 * Unlock mechanism: `sessionStorage.venueAdminAuth_${venueSlug} === 'true'`
 * (set by VenueAdminPinLogin on successful PIN auth).
 *
 * Unauthenticated requests are redirected to the venue PIN unlock screen
 * (`/:venueSlug/admin/login?next=<blocked path>`) so an admin can unlock the
 * device and land right back where they were (e.g. the kiosk).
 */
export default function ProtectedVenueRoute({ children }) {
  const { venueSlug } = useParams();
  const isAuthed =
    typeof window !== "undefined" &&
    sessionStorage.getItem(`venueAdminAuth_${venueSlug}`) === "true";

  // Show the toast only once per redirect (Strict Mode double-invoke safe)
  const toastShown = useRef(false);
  useEffect(() => {
    if (!isAuthed && !toastShown.current) {
      toastShown.current = true;
      toast.error("This device is locked. Enter the venue PIN to unlock.");
    }
  }, [isAuthed]);

  if (!isAuthed) {
    const next = encodeURIComponent(
      typeof window !== "undefined" ? window.location.pathname + window.location.search : ""
    );
    return <Navigate to={`/${venueSlug}/admin/login?next=${next}`} replace />;
  }

  return children;
}
